import { useQuery } from "@tanstack/react-query";
import { type FormEvent, useState } from "react";
import { Link } from "react-router-dom";
import { api, jsonBody } from "../../api/client";
import type { ListResponse } from "../../api/types";
import { useAuth } from "../../auth/AuthProvider";
import { membershipAllows } from "../../auth/permissions";
import { useSensitiveAction } from "../../auth/SensitiveActionGate";
import { formatDate, formatMoney, titleCase } from "../../lib/format";
import {
  Card,
  CardHeader,
  EmptyState,
  ErrorPanel,
  Field,
  FormActions,
  Notice,
  StatusPill,
} from "../../ui/components";
import { PanelSkeleton } from "../../ui/skeletons";

interface Subscription {
  status: string;
  planCode: string;
  billingInterval: "monthly" | "annual";
  pendingPlanCode?: string;
  pendingBillingInterval?: "monthly" | "annual";
  introductoryTrialDays: number;
  billingEmail: string;
  currency: string;
  priceMinor?: number;
  currentPeriodEnd?: string;
  cancelAtPeriodEnd: boolean;
  pastDueSince?: string;
  graceExtensionUntil?: string;
  version: number;
}
interface BillingStatus {
  subscription: Subscription;
  entitlementState: string;
  entitlement: {
    planCode: string;
    planName: string;
    features: Record<string, boolean>;
    limits: Record<string, number>;
  };
}
interface Plan {
  code: string;
  name: string;
  monthlyPriceMinor: number;
  annualPriceMinor: number;
  currency: string;
}
interface CapacityRequest {
  id: string;
  requestedLimits: Record<string, number>;
  requestedFeatures: Record<string, boolean>;
  context: string;
  status: string;
  quotedMonthlyPriceMinor?: number;
  quotedAnnualPriceMinor?: number;
  operatorReason?: string;
  createdAt: string;
}
interface Redirect {
  url: string;
}

// What this workspace pays us. The money the workspace takes from its own customers is a
// different relationship with a different account, and it lives under Integrations so the two are
// never mistaken for each other.
export function BillingSettings() {
  const auth = useAuth();
  const canBill = membershipAllows(auth.session?.selectedMembership, "tenant.manage");
  const sensitive = useSensitiveAction({
    setupTitle: "Protect subscription changes with two-step verification",
    lockedTitle: "Confirm your identity to manage the subscription",
    lockedDescription:
      "These changes affect what this workspace is charged. Enter a fresh code before continuing.",
  });
  const billing = useQuery({
    queryKey: ["billing", "status"],
    queryFn: () => api<BillingStatus>("/api/v1/billing/status"),
    enabled: canBill,
  });
  const plans = useQuery({
    queryKey: ["public", "billing-plans"],
    queryFn: () => api<ListResponse<Plan>>("/api/v1/billing/plans"),
    enabled: canBill,
  });
  const capacityRequests = useQuery({
    queryKey: ["billing", "capacity-requests"],
    queryFn: () => api<ListResponse<CapacityRequest>>("/api/v1/billing/capacity-requests"),
    enabled: canBill,
  });
  const [working, setWorking] = useState(false);
  const [error, setError] = useState<unknown>();
  const [selectedPlan, setSelectedPlan] = useState("business");
  const [billingInterval, setBillingInterval] = useState<"monthly" | "annual">("monthly");
  const [capacitySubmitted, setCapacitySubmitted] = useState(false);

  async function redirect(path: string, body?: unknown) {
    setWorking(true);
    setError(undefined);
    try {
      const result = await api<Redirect>(path, { method: "POST", body: jsonBody(body ?? {}) });
      window.location.assign(result.url);
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
      setWorking(false);
    }
  }

  async function requestCapacity(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const formElement = event.currentTarget;
    const form = new FormData(formElement);
    const requestedLimits = Object.fromEntries(
      ["venues", "locations", "staff", "customDomains", "webhooks"]
        .map((key) => [key, Number(form.get(key) || 0)] as const)
        .filter(([, value]) => Number.isInteger(value) && value > 0),
    );
    const requestedFeatures = Object.fromEntries(
      ["sso", "api", "customDomains"]
        .filter((key) => form.get(`feature-${key}`) === "on")
        .map((key) => [key, true]),
    );
    setWorking(true);
    setError(undefined);
    setCapacitySubmitted(false);
    try {
      await api<CapacityRequest>("/api/v1/billing/capacity-requests", {
        method: "POST",
        body: jsonBody({
          requestedLimits,
          requestedFeatures,
          context: String(form.get("context")),
        }),
      });
      formElement.reset();
      setCapacitySubmitted(true);
      await capacityRequests.refetch();
    } catch (caught) {
      if (!sensitive.markProtectedError(caught)) setError(caught);
    } finally {
      setWorking(false);
    }
  }

  if (canBill && (billing.isLoading || plans.isLoading || capacityRequests.isLoading)) {
    return <PanelSkeleton />;
  }
  return (
    <div className="stack">
      <Notice tone="info" title="Protected controls">
        Starting a subscription, opening the billing portal, and requesting custom capacity require
        a recent two-step verification.
      </Notice>
      {sensitive.view}
      {error || billing.error || plans.error || capacityRequests.error ? (
        <ErrorPanel error={error ?? billing.error ?? plans.error ?? capacityRequests.error} />
      ) : null}
      {capacitySubmitted ? (
        <Notice tone="success">Custom capacity request submitted for a written quote.</Notice>
      ) : null}
      <Card>
        <CardHeader
          title="Platform subscription"
          description="Published package pricing includes the platform, routine hosting, and standard support. Invitation-specific introductory periods are explicit, and there is no hidden platform surcharge."
          actions={billing.data ? <StatusPill value={billing.data.subscription.status} /> : null}
        />
        {canBill && billing.data ? (
          <div className="stack">
            <dl className="definition-list">
              <dt>Package</dt>
              <dd>
                {plans.data?.items.find(
                  (plan) =>
                    plan.code ===
                    (billing.data.subscription.pendingPlanCode ||
                      billing.data.subscription.planCode),
                )?.name ||
                  titleCase(
                    billing.data.subscription.pendingPlanCode || billing.data.subscription.planCode,
                  )}
              </dd>
              <dt>Entitlement</dt>
              <dd>{titleCase(billing.data.entitlementState)}</dd>
              <dt>Billing email</dt>
              <dd>{billing.data.subscription.billingEmail}</dd>
              <dt>Price</dt>
              <dd>
                {billing.data.subscription.priceMinor === undefined
                  ? "Configured at checkout"
                  : formatMoney(
                      billing.data.subscription.priceMinor,
                      billing.data.subscription.currency,
                    )}
              </dd>
              <dt>Billing interval</dt>
              <dd>
                {titleCase(
                  billing.data.subscription.pendingBillingInterval ||
                    billing.data.subscription.billingInterval,
                )}
              </dd>
              <dt>Introductory offer</dt>
              <dd>
                {billing.data.subscription.introductoryTrialDays
                  ? `${billing.data.subscription.introductoryTrialDays} days before the first charge`
                  : "Standard billing"}
              </dd>
              <dt>Period ends</dt>
              <dd>{formatDate(billing.data.subscription.currentPeriodEnd)}</dd>
              <dt>Cancel at period end</dt>
              <dd>{billing.data.subscription.cancelAtPeriodEnd ? "Yes" : "No"}</dd>
            </dl>
            {billing.data.subscription.pastDueSince ? (
              <Notice tone="warning">
                Past due since {formatDate(billing.data.subscription.pastDueSince)}.
              </Notice>
            ) : null}
            {billing.data.subscription.status !== "active" ? (
              <div className="stack">
                {billing.data.subscription.pendingPlanCode ? (
                  <Notice tone="info" title="Invitation-bound offer">
                    The package and billing interval were approved before the owner invitation and
                    cannot be changed during this checkout.
                  </Notice>
                ) : null}
                <div className="form-grid">
                  <Field label="Package">
                    <select
                      value={billing.data.subscription.pendingPlanCode || selectedPlan}
                      disabled={Boolean(billing.data.subscription.pendingPlanCode)}
                      onChange={(event) => setSelectedPlan(event.target.value)}
                    >
                      {plans.data?.items.map((plan) => (
                        <option key={plan.code} value={plan.code}>
                          {plan.name} - {formatMoney(plan.monthlyPriceMinor, plan.currency)}/month
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Billing interval">
                    <select
                      value={billing.data.subscription.pendingBillingInterval || billingInterval}
                      disabled={Boolean(billing.data.subscription.pendingBillingInterval)}
                      onChange={(event) =>
                        setBillingInterval(event.target.value as "monthly" | "annual")
                      }
                    >
                      <option value="monthly">Monthly</option>
                      <option value="annual">Annual, two months included</option>
                    </select>
                  </Field>
                  <div className="full">
                    <button
                      className="button primary"
                      type="button"
                      disabled={working || !sensitive.unlocked}
                      onClick={() =>
                        redirect("/api/v1/billing/subscription-checkout", {
                          planCode: billing.data.subscription.pendingPlanCode || selectedPlan,
                          billingInterval:
                            billing.data.subscription.pendingBillingInterval || billingInterval,
                        })
                      }
                    >
                      Start subscription
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <button
                className="button primary"
                type="button"
                disabled={working || !sensitive.unlocked}
                onClick={() => redirect("/api/v1/billing/portal")}
              >
                Manage billing
              </button>
            )}
            <small>
              Taking payments from your own customers is set up separately under{" "}
              <Link to="/integrations/payments">Integrations</Link>.
            </small>
          </div>
        ) : (
          <Notice tone="warning">
            Only the workspace owner can view or manage subscription billing.
          </Notice>
        )}
      </Card>
      {canBill ? (
        <Card>
          <CardHeader
            title="Custom capacity and pricing"
            description="Request additional venues, locations, staff, domains, webhooks, or enterprise features. Nothing changes until an operator reviews the request and the written quote is accepted."
          />
          <form className="form-grid" onSubmit={requestCapacity}>
            <Field label="Total venues requested">
              <input name="venues" type="number" min={1} max={1000000} />
            </Field>
            <Field label="Total bookable locations requested">
              <input name="locations" type="number" min={1} max={1000000} />
            </Field>
            <Field label="Total staff requested">
              <input name="staff" type="number" min={1} max={1000000} />
            </Field>
            <Field label="Total custom domains requested">
              <input name="customDomains" type="number" min={1} max={1000000} />
            </Field>
            <Field label="Total webhooks requested">
              <input name="webhooks" type="number" min={1} max={1000000} />
            </Field>
            <fieldset>
              <legend>Additional features</legend>
              <label className="check">
                <input name="feature-sso" type="checkbox" /> Workforce SSO
              </label>
              <label className="check">
                <input name="feature-api" type="checkbox" /> Integration API
              </label>
              <label className="check">
                <input name="feature-customDomains" type="checkbox" /> Custom domains
              </label>
            </fieldset>
            <Field
              label="Business context"
              hint="Describe the operating need, timing, and expected capacity. At least 10 characters."
            >
              <textarea name="context" required minLength={10} maxLength={4000} />
            </Field>
            <div className="full">
              <FormActions
                submitting={working}
                disabled={!sensitive.unlocked}
                submitLabel="Request written quote"
              />
            </div>
          </form>
          {capacityRequests.data?.items.length ? (
            <div className="stack">
              {capacityRequests.data.items.map((request) => (
                <div className="embedded-panel" key={request.id}>
                  <div className="inline spread">
                    <strong>{formatDate(request.createdAt)}</strong>
                    <StatusPill value={request.status} />
                  </div>
                  <p>{request.context}</p>
                  <small>
                    Limits:{" "}
                    {Object.entries(request.requestedLimits)
                      .map(([key, value]) => `${titleCase(key)} ${value}`)
                      .join(", ") || "None"}
                  </small>
                  {request.quotedMonthlyPriceMinor !== undefined ? (
                    <p>
                      Quote: {formatMoney(request.quotedMonthlyPriceMinor, "USD")}/month and{" "}
                      {formatMoney(request.quotedAnnualPriceMinor ?? 0, "USD")}/year
                    </p>
                  ) : null}
                  {request.operatorReason ? <small>{request.operatorReason}</small> : null}
                </div>
              ))}
            </div>
          ) : (
            <EmptyState
              title="No custom capacity requests"
              description="Published package limits apply until a written request is reviewed and accepted."
            />
          )}
        </Card>
      ) : null}
    </div>
  );
}
